/**
 * Static asset cache-busting.
 *
 * The problem this solves
 * -----------------------
 * Files in `public/` (hero-home.webp, card-mobile.webp, issalogo.webp …)
 * keep the SAME URL when we replace their bytes. Browsers that fetched
 * the old version with a long `Cache-Control: max-age` will keep serving
 * the stale copy from disk WITHOUT ever asking the server again — so a
 * replaced image looks unchanged for the full TTL (it was 30 days), even
 * after we deploy, purge the edge, and shorten the header. Incognito
 * windows look fine because they use a fresh, empty cache.
 *
 * The fix
 * -------
 * Append a build-version query (`?v=<token>`) to every static asset URL.
 * `/hero-home.webp?v=k3f9` is a brand-new URL the browser has never
 * cached, so it must fetch it. The token is stamped at build time, so
 * every deploy automatically invalidates all static images — no manual
 * purging, no waiting out a TTL.
 *
 * Vite content-hashes everything under `src/assets`, so those don't need
 * this. Only root-absolute `public/` paths (starting with `/`) do.
 *
 * Usage:
 *   <img src={asset('/hero-home.webp')} … />
 *   <HeroBackground image="/hero-courses.webp" />   // wrapped internally
 */

// Stamped at build time by vite `define` (see vite.config.ts). Falls
// back to a stable string in dev so URLs stay clean while iterating.
declare const __ASSET_VERSION__: string
const VERSION =
  typeof __ASSET_VERSION__ === 'string' ? __ASSET_VERSION__ : 'dev'

/**
 * Append the build version to a root-absolute public asset path.
 *
 * Returns the input unchanged when it is:
 *  - empty / nullish
 *  - not root-absolute (`http(s):`, `data:`, `blob:`, relative, or
 *    protocol-relative `//`), since those aren't our cache-frozen
 *    public files.
 */
export function asset(path?: string | null): string {
  if (!path) return path ?? ''
  // Only version our own root-absolute public files.
  if (!path.startsWith('/') || path.startsWith('//')) return path
  const sep = path.includes('?') ? '&' : '?'
  return `${path}${sep}v=${VERSION}`
}
